import { useEffect } from "react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import DepreciationSection from "./components/DepreciationSection";
import VideoSection from "./components/VideoSection";
import InsuranceEstimatesSection from "./components/InsuranceEstimatesSection";
import HoldbackSection from "./components/HoldbackSection";
import CostComparisonSection from "./components/CostComparisonSection";
import CutCornersSection from "./components/CutCornersSection";
import HowWeHelpSection from "./components/HowWeHelpSection";
import ProjectsSection from "./components/ProjectsSection";
import TestimonialsSection from "./components/TestimonialsSection";
import CtaSection from "./components/CtaSection";
import Footer from "./components/Footer";

export default function Home() {
  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "RoofingContractor"],
        "name": "RidgeCap GC",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "logo": "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/3d9f8b58849eb5d2771df5fcd23c8990.png",
        "image": "https://static.wixstatic.com/media/28e51a_fb09017021e941b88dfe3d7c8c9b392b~mv2.jpg/v1/fill/w_1918,h_1080,q_90,enc_avif,quality_auto/28e51a_fb09017021e941b88dfe3d7c8c9b392b~mv2.jpg",
        "description": "RidgeCap GC helps homeowners in Northwest Arkansas and Dallas-Fort Worth maximize insurance roofing claims, recover depreciation holdback funds, and receive manufacturer-certified roof installations.",
        "telephone": ["+14793964040", "+12149458899"],
        "areaServed": [
          { "@type": "City", "name": "Northwest Arkansas", "addressRegion": "AR" },
          { "@type": "City", "name": "Dallas-Fort Worth", "addressRegion": "TX" }
        ],
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "AR",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 36.3729,
          "longitude": -94.2088
        },
        "priceRange": "$$",
        "sameAs": [
          "https://www.facebook.com",
          "https://www.instagram.com"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Roofing Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Insurance Claim Assistance" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Residential Roofing" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Storm Damage Restoration" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Depreciation Recovery" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Supplement Filing" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Inspection" } }
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is depreciation holdback on a roofing insurance claim?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Depreciation holdback, also called recoverable depreciation, is a portion of your insurance claim that the insurer withholds until repairs are completed. The average holdback is $8,000–$15,000, and 98% of homeowners never claim it back."
            }
          },
          {
            "@type": "Question",
            "name": "How does RidgeCap GC help maximize my insurance roofing claim?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "RidgeCap GC reviews your insurance estimate, identifies missing line items, files supplements on your behalf, and ensures all recoverable depreciation is claimed — so you get every dollar you are owed from your insurance company."
            }
          },
          {
            "@type": "Question",
            "name": "Does RidgeCap GC serve both Northwest Arkansas and Dallas-Fort Worth?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. RidgeCap GC has offices serving both Northwest Arkansas (NWA) and the Dallas-Fort Worth (DFW) metro area. Call 479-396-4040 for NWA or 214-945-8899 for DFW."
            }
          },
          {
            "@type": "Question",
            "name": "What is a supplement filing in a roofing insurance claim?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A supplement is an additional claim filed when the original insurance estimate misses line items or undervalues the scope of work. RidgeCap GC identifies these gaps and files supplements to recover the full value of your claim."
            }
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "RidgeCap GC | Insurance Roofing Claim Specialists NWA & DFW",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "description": "RidgeCap GC helps NWA and DFW homeowners maximize insurance roofing claims, recover depreciation holdback funds, and get manufacturer-certified roof installations.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "name": "RidgeCap GC",
          "url": import.meta.env.VITE_SITE_URL || "https://example.com"
        }
      }
    ];

    const existingScripts = document.querySelectorAll('script[data-schema="ridgecap"]');
    existingScripts.forEach((s) => s.remove());

    schemas.forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "ridgecap");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="ridgecap"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <DepreciationSection />
      <VideoSection />
      <InsuranceEstimatesSection />
      <HoldbackSection />
      <CostComparisonSection />
      <CutCornersSection />
      <HowWeHelpSection />
      <ProjectsSection />
      <TestimonialsSection />
      <CtaSection />
      <Footer />
    </div>
  );
}
